import React, { useState, Component } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarIcon, MapPinIcon, UsersIcon, ClockIcon, PlusIcon, PencilIcon, TrashIcon, FilterIcon, ChevronLeftIcon, ChevronRightIcon, LayoutListIcon, BookIcon, UsersIcon as CoAuthorsIcon } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { getBookSpineColor } from '../utils/genreColors';
// Types
type TabType = 'events' | 'releases' | 'cowrites';
interface Event {
  id: string;
  title: string;
  type: string;
  startDate: string;
  endDate: string;
  time: string;
  location: string;
  capacity: number;
  registrations: number;
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  color?: string;
  image?: string;
}
interface BookRelease {
  id: string;
  title: string;
  releaseDate: string;
  genre: string;
  status: 'preorder' | 'coming_soon' | 'released';
  price: number;
  preorders?: number;
  image: string;
  series?: {
    name: string;
    position: number;
  };
  coAuthor?: string;
}
interface HoveredItem {
  type: 'event' | 'book';
  data: Event | BookRelease;
}
// Helper to get current year and month for dynamic dates
const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth();
const formatDate = (year: number, month: number, day: number): string => {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};
// Mock Events
const mockEvents: Event[] = [{
  id: '1',
  title: 'Book Signing: The Silent Echo',
  type: 'Book Signing',
  startDate: formatDate(currentYear, currentMonth, 8),
  endDate: formatDate(currentYear, currentMonth, 8),
  time: '14:00-16:00',
  location: 'Bright Pages Bookstore, Seattle',
  capacity: 50,
  registrations: 32,
  status: 'upcoming',
  color: 'bg-blue-500',
  image: 'https://images.unsplash.com/photo-1528605105345-5344ea20e269?w=400'
}, {
  id: '2',
  title: 'Virtual Q&A Session',
  type: 'Virtual Event',
  startDate: formatDate(currentYear, currentMonth, 14),
  endDate: formatDate(currentYear, currentMonth, 14),
  time: '19:00-20:30',
  location: 'Online (Zoom)',
  capacity: 100,
  registrations: 75,
  status: 'upcoming',
  color: 'bg-purple-500',
  image: 'https://images.unsplash.com/photo-1517971129774-8a2b38fa128e?w=400'
}, {
  id: '3',
  title: 'Literary Festival',
  type: 'Festival',
  startDate: formatDate(currentYear, currentMonth, 18),
  endDate: formatDate(currentYear, currentMonth, 22),
  time: 'All Day',
  location: 'City Convention Center',
  capacity: 1000,
  registrations: 750,
  status: 'upcoming',
  color: 'bg-green-500',
  image: 'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=400'
}, {
  id: '4',
  title: 'Writing Workshop Series',
  type: 'Workshop',
  startDate: formatDate(currentYear, currentMonth, 10),
  endDate: formatDate(currentYear, currentMonth, 12),
  time: '10:00-12:00',
  location: 'Community Center, Seattle',
  capacity: 25,
  registrations: 15,
  status: 'upcoming',
  color: 'bg-amber-500',
  image: 'https://images.unsplash.com/photo-1532012197267-da84d127e765?w=400'
}, {
  id: '5',
  title: 'Author Meet & Greet Tour',
  type: 'Meet & Greet',
  startDate: formatDate(currentYear, currentMonth, 25),
  endDate: formatDate(currentYear, currentMonth, 28),
  time: '15:00-17:00',
  location: 'Various Locations',
  capacity: 200,
  registrations: 180,
  status: 'upcoming',
  color: 'bg-rose-500',
  image: 'https://images.unsplash.com/photo-1528605105345-5344ea20e269?w=400'
}];
// Mock Book Releases
const mockReleases: BookRelease[] = [{
  id: 'b1',
  title: 'The Silent Echo',
  releaseDate: formatDate(currentYear, currentMonth, 15),
  genre: 'Romance',
  status: 'preorder',
  price: 24.99,
  preorders: 342,
  image: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400',
  series: {
    name: 'Echo Series',
    position: 1
  }
}, {
  id: 'b2',
  title: 'Midnight Whispers',
  releaseDate: formatDate(currentYear, currentMonth, 22),
  genre: 'Fantasy',
  status: 'coming_soon',
  price: 19.99,
  preorders: 156,
  image: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?w=400'
}, {
  id: 'b3',
  title: "Dragon's Crown",
  releaseDate: formatDate(currentYear, currentMonth + 1, 5),
  genre: 'Fantasy',
  status: 'preorder',
  price: 29.99,
  preorders: 89,
  image: 'https://images.unsplash.com/photo-1626788353765-b55f9fc8daf9?w=400',
  series: {
    name: 'Crown Saga',
    position: 3
  }
}];
// Mock Co-Writes
const mockCoWrites: BookRelease[] = [{
  id: 'c1',
  title: 'Shadows of Tomorrow',
  releaseDate: formatDate(currentYear, currentMonth, 20),
  genre: 'Sci-Fi',
  status: 'preorder',
  price: 22.99,
  preorders: 215,
  image: 'https://images.unsplash.com/photo-1633477189729-9290b3261d0a?w=400',
  coAuthor: 'Sarah Chen'
}, {
  id: 'c2',
  title: 'The Last Summer',
  releaseDate: formatDate(currentYear, currentMonth + 1, 10),
  genre: 'Romance',
  status: 'coming_soon',
  price: 18.99,
  preorders: 78,
  image: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400',
  coAuthor: 'James Miller'
}];
const getFirstDayOfMonth = (date: Date): number => {
  return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
};
const getDaysInMonth = (date: Date): number => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
};
const formatDateString = (year: number, month: number, day: number): string => {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};
// Helper to get item color
const getItemColor = (item: Event | BookRelease, type: 'event' | 'book'): string => {
  if (type === 'event') {
    const event = item as Event;
    return event.color?.replace('bg-', '') || 'indigo-500';
  }
  const book = item as BookRelease;
  return getBookSpineColor(book.genre);
};
const ManageEventShowings = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const navigate = useNavigate();
  const [events] = useState<Event[]>(mockEvents);
  const [releases] = useState<BookRelease[]>(mockReleases);
  const [coWrites] = useState<BookRelease[]>(mockCoWrites);
  const [activeTab, setActiveTab] = useState<TabType>('events');
  const [activeFilter, setActiveFilter] = useState<'all' | Event['status']>('all');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [hoveredItem, setHoveredItem] = useState<HoveredItem | null>(null);
  const [hoverPosition, setHoverPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const getStatusColor = (status: Event['status']) => {
    switch (status) {
      case 'upcoming':
        return darkMode ? 'text-green-400 bg-green-900/30' : 'text-green-700 bg-green-100';
      case 'ongoing':
        return darkMode ? 'text-blue-400 bg-blue-900/30' : 'text-blue-700 bg-blue-100';
      case 'completed':
        return darkMode ? 'text-gray-400 bg-gray-700' : 'text-gray-700 bg-gray-100';
      case 'cancelled':
        return darkMode ? 'text-red-400 bg-red-900/30' : 'text-red-700 bg-red-100';
      default:
        return darkMode ? 'text-gray-400 bg-gray-700' : 'text-gray-700 bg-gray-100';
    }
  };
  const getBookStatusColor = (status: BookRelease['status']) => {
    switch (status) {
      case 'preorder':
        return darkMode ? 'text-yellow-400 bg-yellow-900/30' : 'text-yellow-700 bg-yellow-100';
      case 'coming_soon':
        return darkMode ? 'text-blue-400 bg-blue-900/30' : 'text-blue-700 bg-blue-100';
      case 'released':
        return darkMode ? 'text-green-400 bg-green-900/30' : 'text-green-700 bg-green-100';
      default:
        return darkMode ? 'text-gray-400 bg-gray-700' : 'text-gray-700 bg-gray-100';
    }
  };
  const isDateInRange = (date: string, event: Event) => {
    return date >= event.startDate && date <= event.endDate;
  };
  const getEventsForDate = (dateString: string) => {
    return events.filter(event => isDateInRange(dateString, event)).sort((a, b) => a.time.localeCompare(b.time));
  };
  const getReleasesForDate = (dateString: string) => {
    return releases.filter(book => book.releaseDate === dateString);
  };
  const getCoWritesForDate = (dateString: string) => {
    return coWrites.filter(book => book.releaseDate === dateString);
  };
  // Get all items for a date based on active tab
  const getItemsForDate = (dateString: string) => {
    if (activeTab === 'events') return {
      events: getEventsForDate(dateString),
      books: []
    };
    if (activeTab === 'releases') return {
      events: [],
      books: getReleasesForDate(dateString)
    };
    if (activeTab === 'cowrites') return {
      events: [],
      books: getCoWritesForDate(dateString)
    };
    return {
      events: [],
      books: []
    };
  };
  // Handle mouse move for hover card positioning
  const handleMouseMove = (e: React.MouseEvent, item: Event | BookRelease, type: 'event' | 'book') => {
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const cardHeight = 220;
    const cardWidth = 300;
    let x = e.clientX + 15;
    let y = e.clientY + 15;
    if (x + cardWidth > viewportWidth - 20) {
      x = e.clientX - cardWidth - 15;
    }
    if (y + cardHeight > viewportHeight - 20) {
      y = e.clientY - cardHeight - 15;
    }
    if (y < 20) {
      y = 20;
    }
    setHoverPosition({
      x,
      y
    });
    setHoveredItem({
      type,
      data: item
    });
  };
  const handleMouseLeave = () => {
    setHoveredItem(null);
    setHoverPosition(null);
  };
  // Navigate to appropriate edit page
  const handleItemClick = (item: Event | BookRelease, type: 'event' | 'book') => {
    if (type === 'event') {
      navigate(`/vendors/event-manager?id=${(item as Event).id}`);
    } else {
      navigate(`/vendors/manage?type=book&id=${(item as BookRelease).id}`);
    }
  };
  // Hover Card Component
  const HoverCard = () => {
    if (!hoveredItem || !hoverPosition) return null;
    const {
      type,
      data
    } = hoveredItem;
    if (type === 'event') {
      const event = data as Event;
      return <div className={`fixed z-[9999] w-[300px] p-4 rounded-xl shadow-xl border pointer-events-none ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'}`} style={{
        left: hoverPosition.x,
        top: hoverPosition.y
      }} data-id="element-10167" data-mp="170uw59lkerng">
          <div className="flex gap-3" data-id="element-10168" data-mp="11yn8vbh1qal8">
            {event.image && <img src={event.image} alt={event.title} className="w-20 h-28 object-cover rounded-lg shadow-sm flex-shrink-0" data-id="element-10169" data-mp="11wiz4cwguhd1" />}
            <div className="flex-1 min-w-0" data-id="element-10170" data-mp="1at1ndbtr5h3a">
              <h4 className="font-bold text-sm leading-tight mb-1 line-clamp-2" data-id="element-10171" data-mp="1foqt5wwd7u81">
                {event.title}
              </h4>
              <span className={`inline-block text-[10px] uppercase font-bold px-2 py-0.5 rounded-full mb-2 ${darkMode ? 'bg-indigo-900 text-indigo-200' : 'bg-indigo-100 text-indigo-800'}`} data-id="element-10172" data-mp="1kpmty1lf7098">
                {event.type}
              </span>
              <div className="space-y-1" data-id="element-10173" data-mp="3rgci71f1vx34">
                <div className="flex items-center text-xs" data-id="element-10174" data-mp="3rhfcljbl0hns">
                  <CalendarIcon className="h-3 w-3 mr-1.5 opacity-70" data-id="element-10175" data-mp="3jfeo98twog09" />
                  <span data-id="element-10176" data-mp="3eg20mlv7n4wm">
                    {new Date(event.startDate).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric'
                  })}
                  </span>
                </div>
                <div className="flex items-center text-xs" data-id="element-10177" data-mp="3ocevhn2hrosy">
                  <ClockIcon className="h-3 w-3 mr-1.5 opacity-70" data-id="element-10178" data-mp="67bkxmtrakl5" />
                  <span data-id="element-10179" data-mp="190b3c3goa2x">{event.time}</span>
                </div>
                <div className="flex items-center text-xs" data-id="element-10180" data-mp="18qkpnei80wp">
                  <MapPinIcon className="h-3 w-3 mr-1.5 opacity-70" data-id="element-10181" data-mp="fy69wyydzyaa" />
                  <span className="truncate" data-id="element-10182" data-mp="aytli9rvaj5l">{event.location}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between" data-id="element-10183" data-mp="ozmj53dgtucx">
            <div className="flex items-center text-xs" data-id="element-10184" data-mp="kx9g4mq3saju">
              <UsersIcon className="h-3 w-3 mr-1 opacity-70" data-id="element-10185" data-mp="kyuaab8i9rh6" />
              <span data-id="element-10186" data-mp="13l1sd273w3n8">
                {event.registrations}/{event.capacity} registered
              </span>
            </div>
            <span className={`text-xs font-medium ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} data-id="element-10187" data-mp="18juuxzkuua8m">
              Click to edit →
            </span>
          </div>
        </div>;
    }
    // Book hover card
    const book = data as BookRelease;
    return <div className={`fixed z-[9999] w-[300px] p-4 rounded-xl shadow-xl border pointer-events-none ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'}`} style={{
      left: hoverPosition.x,
      top: hoverPosition.y
    }} data-id="element-10188" data-mp="ku0l7qvu11sl">
        <div className="flex gap-3" data-id="element-10189" data-mp="fw8pels2gvj4">
          <img src={book.image} alt={book.title} className="w-16 h-24 object-cover rounded-md shadow-sm flex-shrink-0" data-id="element-10190" data-mp="1vxxrgixncww" />
          <div className="flex-1 min-w-0" data-id="element-10191" data-mp="6u98dth1xrg6">
            <h4 className="font-bold text-sm leading-tight mb-1 line-clamp-2" data-id="element-10192" data-mp="19872xxenkm6n">
              {book.title}
            </h4>
            {book.coAuthor && <p className={`text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10193" data-mp="157n6kw1wlmlk">
                with {book.coAuthor}
              </p>}
            {book.series && <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full mb-2 ${darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'}`} data-id="element-10194" data-mp="qhxsk08ppo3v">
                {book.series.name} #{book.series.position}
              </span>}
            <div className="space-y-1" data-id="element-10195" data-mp="ci67xgnox9gw">
              <div className="flex items-center text-xs" data-id="element-10196" data-mp="cj7x0p1jjwhm">
                <CalendarIcon className="h-3 w-3 mr-1.5 opacity-70" data-id="element-10197" data-mp="2bvabfkvb60of" />
                <span data-id="element-10198" data-mp="2buirj7b2prdz">
                  Release:{' '}
                  {new Date(book.releaseDate).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric'
                })}
                </span>
              </div>
              <div className="font-bold text-sm" data-id="element-10199" data-mp="2jdrpm4e4q3nq">${book.price}</div>
            </div>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between" data-id="element-10200" data-mp="2o6ukcfun7j5i">
          <div className="flex gap-2" data-id="element-10201" data-mp="2t54fwrka4lrn">
            <span className="px-2 py-0.5 text-[10px] uppercase font-bold rounded-full text-white" style={{
            backgroundColor: getBookSpineColor(book.genre)
          }} data-id="element-10202" data-mp="2t436ssmtq9ck">
              {book.genre}
            </span>
            {book.preorders && <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10203" data-mp="25dqp4q1s0y8s">
                {book.preorders} preorders
              </span>}
          </div>
          <span className={`text-xs font-medium ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} data-id="element-10204" data-mp="3gwjnayqwxt9r">
            Click to edit →
          </span>
        </div>
      </div>;
  };
  const renderCalendarDay = (day: number) => {
    const dateString = formatDateString(currentDate.getFullYear(), currentDate.getMonth(), day);
    const {
      events: eventsOnDay,
      books: booksOnDay
    } = getItemsForDate(dateString);
    const isSelected = selectedDate === dateString;
    const allItems = [...eventsOnDay.map(e => ({
      type: 'event' as const,
      data: e
    })), ...booksOnDay.map(b => ({
      type: 'book' as const,
      data: b
    }))];
    return <div key={day} onClick={() => setSelectedDate(dateString)} className={`min-h-[120px] p-2 border relative cursor-pointer transition-colors ${darkMode ? `border-gray-700 ${isSelected ? 'ring-2 ring-amber-500 bg-gray-700' : 'hover:bg-gray-700/50'}` : `border-gray-200 ${isSelected ? 'ring-2 ring-purple-500 bg-purple-50' : 'hover:bg-gray-50'}`}`} data-id="element-10205" data-mp="1bt4hh4f0tnd2">
        <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-10206" data-mp="2q12gwjrnw30f">
          {day}
        </span>
        <div className="mt-1 space-y-1" data-id="element-10207" data-mp="2usc65j9at4bs">
          {allItems.slice(0, 3).map((item, idx) => {
          if (item.type === 'event') {
            const event = item.data as Event;
            const isFirstDay = dateString === event.startDate;
            const isLastDay = dateString === event.endDate;
            const isMultiDay = event.startDate !== event.endDate;
            return <div key={`event-${event.id}`} onClick={e => {
              e.stopPropagation();
              handleItemClick(event, 'event');
            }} onMouseMove={e => handleMouseMove(e, event, 'event')} onMouseLeave={handleMouseLeave} className={`text-xs text-white p-1 truncate cursor-pointer hover:opacity-80 transition-opacity ${event.color} ${isMultiDay ? `${isFirstDay ? 'rounded-l' : ''} ${isLastDay ? 'rounded-r' : ''} ${!isFirstDay ? '-ml-2' : ''} ${!isLastDay ? '-mr-2' : ''}` : 'rounded'}`} data-id="element-10208" data-mp="3dp3kde9zgt8z">
                  {(isFirstDay || !isMultiDay) && event.title}
                </div>;
          } else {
            const book = item.data as BookRelease;
            return <div key={`book-${book.id}`} onClick={e => {
              e.stopPropagation();
              handleItemClick(book, 'book');
            }} onMouseMove={e => handleMouseMove(e, book, 'book')} onMouseLeave={handleMouseLeave} className="text-xs text-white p-1 rounded truncate cursor-pointer hover:opacity-80 transition-opacity" style={{
              backgroundColor: getBookSpineColor(book.genre)
            }} data-id="element-10209" data-mp="2638ajvabw3ck">
                  📚 {book.title}
                </div>;
          }
        })}
          {allItems.length > 3 && <div className={`text-[10px] font-medium px-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10210" data-mp="3tyd15bf4go4j">
              +{allItems.length - 3} more
            </div>}
        </div>
      </div>;
  };
  const filteredEvents = events.filter(event => activeFilter === 'all' || event.status === activeFilter);
  const currentBooks = activeTab === 'releases' ? releases : activeTab === 'cowrites' ? coWrites : [];
  return <div className={`min-h-screen py-8 ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'}`} data-id="element-10211" data-mp="lj326c6j3z0z">
      {/* Hover Card */}
      <HoverCard data-id="element-10212" data-mp="qhebmlsww7io" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-10213" data-mp="qjqud16hulws">
        <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4" data-id="element-10214" data-mp="viafx0h6dvom">
          <div data-id="element-10215" data-mp="10glq5cbdu87d">
            <h1 className="text-2xl font-bold" data-id="element-10216" data-mp="10dzlpljsg8td">Manage Events & Releases</h1>
            <p className={`mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10217" data-mp="1yzzr5dh5qa42">
              Track your book signings, releases, and collaborations
            </p>
          </div>
          <div className={`flex items-center gap-1 p-1.5 rounded-lg shadow-sm ${darkMode ? 'bg-gray-800' : 'bg-white'}`} data-id="element-10218" data-mp="28vkt5bm87adt">
            <button onClick={() => setViewMode('calendar')} className={`p-2 rounded-md transition-colors ${viewMode === 'calendar' ? darkMode ? 'bg-amber-900/50 text-amber-400' : 'bg-purple-100 text-purple-600' : darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`} data-id="element-10219" data-mp="2hy3t9nymn8wd">
              <CalendarIcon className="h-5 w-5" data-id="element-10220" data-mp="31ljh13oop1gw" />
            </button>
            <button onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? darkMode ? 'bg-amber-900/50 text-amber-400' : 'bg-purple-100 text-purple-600' : darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`} data-id="element-10221" data-mp="31ne34lboyzmk">
              <LayoutListIcon className="h-5 w-5" data-id="element-10222" data-mp="kzwfk0cxdb0y" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className={`border-b mb-6 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-10223" data-mp="g1uuj9vxv1mu">
          <nav className="-mb-px flex space-x-8" data-id="element-10224" data-mp="1dplhcfxglf3m">
            <button onClick={() => setActiveTab('events')} className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${activeTab === 'events' ? darkMode ? 'border-amber-400 text-amber-400' : 'border-purple-500 text-purple-600' : darkMode ? 'border-transparent text-gray-400 hover:text-gray-200' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-10225" data-mp="18pqr8elictjm">
              <CalendarIcon className="h-4 w-4" data-id="element-10226" data-mp="1rlgsfzlm9lwg" />
              Events
            </button>
            <button onClick={() => setActiveTab('releases')} className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${activeTab === 'releases' ? darkMode ? 'border-amber-400 text-amber-400' : 'border-purple-500 text-purple-600' : darkMode ? 'border-transparent text-gray-400 hover:text-gray-200' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-10227" data-mp="3lw74hkjy5kxt">
              <BookIcon className="h-4 w-4" data-id="element-10228" data-mp="3769er06dcn6i" />
              My Releases
            </button>
            <button onClick={() => setActiveTab('cowrites')} className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${activeTab === 'cowrites' ? darkMode ? 'border-amber-400 text-amber-400' : 'border-purple-500 text-purple-600' : darkMode ? 'border-transparent text-gray-400 hover:text-gray-200' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-10229" data-mp="2t6q86bt7hjwb">
              <CoAuthorsIcon className="h-4 w-4" data-id="element-10230" data-mp="16j6ejp9nn8i9" />
              Co-Writes
            </button>
          </nav>
        </div>

        {viewMode === 'calendar' ? <div className={`rounded-xl shadow-sm overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`} data-id="element-10231" data-mp="1fagh262nmez3">
            <div className={`p-4 border-b flex justify-between items-center ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-10232" data-mp="1aix9842ay8s2">
              <h2 className="text-lg font-medium" data-id="element-10233" data-mp="1k9rw9andhcek">
                {activeTab === 'events' ? 'Event Calendar' : activeTab === 'releases' ? 'Release Calendar' : 'Co-Write Calendar'}
              </h2>
              <div className="flex items-center space-x-4" data-id="element-10234" data-mp="1u5uyshm4zd21">
                <button onClick={() => {
              const newDate = new Date(currentDate);
              newDate.setMonth(currentDate.getMonth() - 1);
              setCurrentDate(newDate);
            }} className={`p-2 rounded-full transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`} data-id="element-10235" data-mp="3wkz6pkd7s46">
                  <ChevronLeftIcon className="h-5 w-5" data-id="element-10236" data-mp="abjvbsmyo31w" />
                </button>
                <span className="text-lg font-medium min-w-[160px] text-center" data-id="element-10237" data-mp="famskqwp94zb">
                  {currentDate.toLocaleString('default', {
                month: 'long',
                year: 'numeric'
              })}
                </span>
                <button onClick={() => {
              const newDate = new Date(currentDate);
              newDate.setMonth(currentDate.getMonth() + 1);
              setCurrentDate(newDate);
            }} className={`p-2 rounded-full transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`} data-id="element-10238" data-mp="3irv46t0u38ll">
                  <ChevronRightIcon className="h-5 w-5" data-id="element-10239" data-mp="2zzj4yf5h8nfo" />
                </button>
              </div>
            </div>
            <div className="p-4" data-id="element-10240" data-mp="2sfi85ydj1xbf">
              <div className="grid grid-cols-7 gap-1 mb-1" data-id="element-10241" data-mp="2nhzzm93f9k3y">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day} className={`text-sm font-medium text-center p-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10242" data-mp="2ilhu3709phqk">
                      {day}
                    </div>)}
              </div>
              <div className="grid grid-cols-7 gap-1" data-id="element-10243" data-mp="24q78kk7wy8yv">
                {Array.from({
              length: getFirstDayOfMonth(currentDate)
            }).map((_, i) => <div key={`empty-${i}`} className={`min-h-[120px] ${darkMode ? 'bg-gray-900/30' : 'bg-gray-50'}`} data-id="element-10244" data-mp="1urrdk65rmfk3" data-mpi={i} />)}
                {Array.from({
              length: getDaysInMonth(currentDate)
            }).map((_, i) => renderCalendarDay(i + 1))}
              </div>
            </div>

            {/* Selected Date Details */}
            {selectedDate && <div className={`border-t p-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-10245" data-mp="1285rif2fh72m">
                <h3 className="text-lg font-medium mb-4" data-id="element-10246" data-mp="176z4859w4u1q">
                  {new Date(selectedDate).toLocaleDateString(undefined, {
              weekday: 'long',
              month: 'long',
              day: 'numeric'
            })}
                </h3>
                <div className="space-y-3" data-id="element-10247" data-mp="1l56vbwk8x14z">
                  {activeTab === 'events' && getEventsForDate(selectedDate).map(event => <Link key={event.id} to={`/vendors/manage?type=event&id=${event.id}`} className={`flex items-start justify-between p-4 rounded-lg transition-colors ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'}`} data-id="element-10248" data-mp="2dv5sygcia6l9">
                        <div data-id="element-10249" data-mp="270y2wrakja5">
                          <h4 className="font-medium" data-id="element-10250" data-mp="25pt4tm415hg">{event.title}</h4>
                          <div className="mt-1 space-y-1" data-id="element-10251" data-mp="fyfjfa8o8wkd">
                            <div className={`flex items-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10252" data-mp="bwtz53tt5g05">
                              <ClockIcon className="h-4 w-4 mr-2" data-id="element-10253" data-mp="3fec3h1adg4vt" />
                              {event.time}
                            </div>
                            <div className={`flex items-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10254" data-mp="3temuc4boljgz">
                              <MapPinIcon className="h-4 w-4 mr-2" data-id="element-10255" data-mp="18mtd5dsq96q4" />
                              {event.location}
                            </div>
                          </div>
                        </div>
                        <span className={`text-sm font-medium ${darkMode ? 'text-amber-400' : 'text-purple-600'}`} data-id="element-10256" data-mp="1nrp9h7lscbg4">
                          Edit →
                        </span>
                      </Link>)}
                  {(activeTab === 'releases' || activeTab === 'cowrites') && (activeTab === 'releases' ? getReleasesForDate(selectedDate) : getCoWritesForDate(selectedDate)).map(book => <Link key={book.id} to={`/vendors/manage?type=book&id=${book.id}`} className={`flex items-start justify-between p-4 rounded-lg transition-colors ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'}`} data-id="element-10257" data-mp="2gfbjaxllwcbf">
                        <div className="flex gap-3" data-id="element-10258" data-mp="q3nfo776g4tv">
                          <img src={book.image} alt={book.title} className="w-12 h-18 object-cover rounded" data-id="element-10259" data-mp="ijcxkg54nvq9" />
                          <div data-id="element-10260" data-mp="8t1n0aqxuoae">
                            <h4 className="font-medium" data-id="element-10261" data-mp="8u316o393qwc">{book.title}</h4>
                            {book.coAuthor && <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10262" data-mp="4pvdmu8j4gzp">
                                with {book.coAuthor}
                              </p>}
                            <div className="flex items-center gap-2 mt-1" data-id="element-10263" data-mp="sfpmz1tn3nf7">
                              <span className="px-2 py-0.5 text-[10px] uppercase font-bold rounded-full text-white" style={{
                      backgroundColor: getBookSpineColor(book.genre)
                    }} data-id="element-10264" data-mp="nhefuyuqbr0o">
                                {book.genre}
                              </span>
                              <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10265" data-mp="2fadzkkdd2itj">
                                ${book.price}
                              </span>
                            </div>
                          </div>
                        </div>
                        <span className={`text-sm font-medium ${darkMode ? 'text-amber-400' : 'text-purple-600'}`} data-id="element-10266" data-mp="2kajnpqkf9n6v">
                          Edit →
                        </span>
                      </Link>)}
                  {getItemsForDate(selectedDate).events.length === 0 && getItemsForDate(selectedDate).books.length === 0 && <p className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10267" data-mp="3aiplj71n9k68">
                        No {activeTab === 'events' ? 'events' : 'releases'} on
                        this date
                      </p>}
                </div>
              </div>}
          </div> /* List View */ : <div className={`rounded-xl shadow-sm overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`} data-id="element-10268" data-mp="2rrexqyr7tnik">
            {activeTab === 'events' ? <>
                <div className={`p-4 border-b flex justify-between items-center ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-10269" data-mp="2irrx4f6b8lqk">
                  <div className="flex space-x-2" data-id="element-10270" data-mp="2muokej6sp60u">
                    {['all', 'upcoming', 'completed'].map(filter => <button key={filter} onClick={() => setActiveFilter(filter as typeof activeFilter)} className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeFilter === filter ? darkMode ? 'bg-amber-900/50 text-amber-400' : 'bg-purple-100 text-purple-700' : darkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-10271" data-mp="l20eu36oi6hw">
                        {filter.charAt(0).toUpperCase() + filter.slice(1)}
                      </button>)}
                  </div>
                  <Link to="/vendors/event-manager" className={`flex items-center px-4 py-2 rounded-lg transition-colors ${darkMode ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-purple-600 hover:bg-purple-700 text-white'}`} data-id="element-10272" data-mp="28ppbxtjlmets">
                    <PlusIcon className="h-5 w-5 mr-2" data-id="element-10273" data-mp="2wf0ixsrjl5gd" />
                    Add New Event
                  </Link>
                </div>
                <div className="overflow-x-auto" data-id="element-10274" data-mp="2yxvhjw72bg3p">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-10275" data-mp="2yz6kxuwlilua">
                    <thead className={darkMode ? 'bg-gray-900/50' : 'bg-gray-50'} data-id="element-10276" data-mp="33wow7ry35d5g">
                      <tr data-id="element-10277" data-mp="38xw19j8ufy8f">
                        {['Event Details', 'Date & Time', 'Location', 'Capacity', 'Status', 'Actions'].map(header => <th key={header} className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10278" data-mp="3rlr9yoplmluo">
                            {header}
                          </th>)}
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`} data-id="element-10279" data-mp="3rya283y662cj">
                      {filteredEvents.map(event => <tr key={event.id} className={`cursor-pointer transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`} onClick={() => navigate(`/manage-events/${event.id}`)} onMouseMove={e => handleMouseMove(e, event, 'event')} onMouseLeave={handleMouseLeave} data-id="element-10280" data-mp="5ifm0czb7phl">
                          <td className="px-6 py-4" data-id="element-10281" data-mp="3dy93au1gt4zp">
                            <div className="font-medium" data-id="element-10282" data-mp="3e1n5o2a4pe75">{event.title}</div>
                            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10283" data-mp="y6bh5d98d9c9">
                              {event.type}
                            </div>
                          </td>
                          <td className="px-6 py-4" data-id="element-10284" data-mp="2g6u9b9nc8s15">
                            <div className={`flex items-center text-sm ${darkMode ? 'text-gray-300' : 'text-gray-900'}`} data-id="element-10285" data-mp="2g3ewyel1tx3f">
                              <CalendarIcon className={`h-4 w-4 mr-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-10286" data-mp="27778oq2fnq95" />
                              {new Date(event.startDate).toLocaleDateString()}
                            </div>
                            <div className={`flex items-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10287" data-mp="2l4m4qdn6ywa3">
                              <ClockIcon className={`h-4 w-4 mr-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-10288" data-mp="3drypci9sku0r" />
                              {event.time}
                            </div>
                          </td>
                          <td className="px-6 py-4" data-id="element-10289" data-mp="3ni6w1clwg4nq">
                            <div className={`flex items-center text-sm ${darkMode ? 'text-gray-300' : 'text-gray-900'}`} data-id="element-10290" data-mp="3ii2h3r0n3hzm">
                              <MapPinIcon className={`h-4 w-4 mr-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-10291" data-mp="3dl29hbo5676e" />
                              {event.location}
                            </div>
                          </td>
                          <td className="px-6 py-4" data-id="element-10292" data-mp="2zruldtz9rttg">
                            <div className={`flex items-center text-sm ${darkMode ? 'text-gray-300' : 'text-gray-900'}`} data-id="element-10293" data-mp="2ut9h9jgwcpzq">
                              <UsersIcon className={`h-4 w-4 mr-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-10294" data-mp="2ls1xs7j1htpp" />
                              {event.registrations}/{event.capacity}
                            </div>
                            <div className={`w-full rounded-full h-2 mt-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} data-id="element-10295" data-mp="15d750tj0t7s9">
                              <div className={`rounded-full h-2 ${darkMode ? 'bg-amber-500' : 'bg-purple-600'}`} style={{
                        width: `${event.registrations / event.capacity * 100}%`
                      }} data-id="element-10296" data-mp="1f7fxxibyr2qy" />
                            </div>
                          </td>
                          <td className="px-6 py-4" data-id="element-10297" data-mp="wchfg7kcsdmz">
                            <span className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium ${getStatusColor(event.status)}`} data-id="element-10298" data-mp="3uzn2o1mwy3a2">
                              {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4" data-id="element-10299" data-mp="32dwp0p2bt0ps">
                            <div className="flex items-center space-x-3" data-id="element-10300" data-mp="2xe0pb20nvvg1">
                              <button onClick={e => {
                        e.stopPropagation();
                        navigate(`/vendors/event-manager?id=${event.id}`);
                      }} className={darkMode ? 'text-amber-400 hover:text-amber-300' : 'text-purple-600 hover:text-purple-900'} data-id="element-10301" data-mp="2xg51s8cmm2rx">
                                <PencilIcon className="h-5 w-5" data-id="element-10302" data-mp="dik7br580qtc" />
                              </button>
                              <button onClick={e => e.stopPropagation()} className="text-red-600 hover:text-red-900" data-id="element-10303" data-mp="iigeu0d4n0dn">
                                <TrashIcon className="h-5 w-5" data-id="element-10304" data-mp="8ipjjwgqshci" />
                              </button>
                            </div>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </> /* Books List View */ : <>
                <div className={`p-4 border-b flex justify-between items-center ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-10305" data-mp="3lyngy2fw8pkb">
                  <h2 className="text-lg font-medium" data-id="element-10306" data-mp="3818mgjas9rlh">
                    {activeTab === 'releases' ? 'My Book Releases' : 'Co-Written Books'}
                  </h2>
                  <Link to="/vendors/manage?type=book" className={`flex items-center px-4 py-2 rounded-lg transition-colors ${darkMode ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-purple-600 hover:bg-purple-700 text-white'}`} data-id="element-10307" data-mp="2xmxkdnl6ljui">
                    <PlusIcon className="h-5 w-5 mr-2" data-id="element-10308" data-mp="2nurj1n91hq2n" />
                    Add New Book
                  </Link>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-10309" data-mp="2ewplxisffhkn">
                  {currentBooks.map(book => <Link key={book.id} to={`/vendors/manage?type=book&id=${book.id}`} className={`flex gap-4 p-4 rounded-xl transition-all hover:shadow-md ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-white'}`} onMouseMove={e => handleMouseMove(e, book, 'book')} onMouseLeave={handleMouseLeave} data-id="element-10310" data-mp="20yhh6op5fbyx">
                      <img src={book.image} alt={book.title} className="w-20 h-28 object-cover rounded-lg shadow-sm" data-id="element-10311" data-mp="1jo57hvvjf2bn" />
                      <div className="flex-1 min-w-0" data-id="element-10312" data-mp="1smzaq53diqy6">
                        <h3 className="font-bold truncate" data-id="element-10313" data-mp="27cwt24emgmfg">{book.title}</h3>
                        {book.coAuthor && <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10314" data-mp="22ed7i53xxcnm">
                            with {book.coAuthor}
                          </p>}
                        {book.series && <p className={`text-xs ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} data-id="element-10315" data-mp="lz28h7mlh3fl">
                            {book.series.name} #{book.series.position}
                          </p>}
                        <div className="flex items-center gap-2 mt-2" data-id="element-10316" data-mp="2m1l64c73r112">
                          <span className="px-2 py-0.5 text-[10px] uppercase font-bold rounded-full text-white" style={{
                    backgroundColor: getBookSpineColor(book.genre)
                  }} data-id="element-10317" data-mp="2m3xourkopff6">
                            {book.genre}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${getBookStatusColor(book.status)}`} data-id="element-10318" data-mp="34yuum7rab4p6">
                            {book.status.replace('_', ' ')}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-2" data-id="element-10319" data-mp="3nqxd7onhkv22">
                          <span className="font-bold" data-id="element-10320" data-mp="3noucoaopjjj3">${book.price}</span>
                          {book.preorders && <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10321" data-mp="1750eccocrv6e">
                              {book.preorders} preorders
                            </span>}
                        </div>
                      </div>
                    </Link>)}
                </div>
              </>}
          </div>}
      </div>
    </div>;
};
export default ManageEventShowings;